import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as bookingApi from '@/services/bookingApi';

const CUSTOMERS_PER_PAGE = 20;

const CustomerList: React.FC = () => {
    const { t } = useTranslation('dashboard');
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [showBannedOnly, setShowBannedOnly] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    const { data: customersData, isLoading, error, refetch } = useQuery({
        queryKey: ['customers', { search: searchQuery, banned: showBannedOnly }],
        queryFn: () => bookingApi.getCustomers({ 
            search: searchQuery || undefined, 
            banned: showBannedOnly || undefined 
        }),
    });

    // Handle both paginated response and potential defensive cases
    const customers = customersData?.data ?? [];
    const totalCustomers = customersData?.total ?? 0;
    const totalPages = customersData?.last_page ?? 1;

    const paginatedCustomers = customers.slice(
        (currentPage - 1) * CUSTOMERS_PER_PAGE,
        currentPage * CUSTOMERS_PER_PAGE
    );

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const handleBannedFilterToggle = () => {
        setShowBannedOnly(!showBannedOnly);
        setCurrentPage(1);
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">{t('customers.title')}</h1>
            </div>

            {/* Search and Filter Bar */}
            <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                    {/* Search Input */}
                    <div className="flex-1">
                        <input
                            type="text"
                            placeholder={t('customers.searchPlaceholder')}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                        />
                    </div>

                    {/* Banned Filter Toggle */}
                    <button
                        onClick={handleBannedFilterToggle}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            showBannedOnly
                                ? 'bg-red-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        {t('customers.showBannedOnly')}
                    </button>
                </div>
            </div>

            {/* Customers Table */}
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                {isLoading ? (
                    <div className="p-8 text-center">
                        <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
                        <p className="mt-4 text-gray-600">{t('customers.loading')}</p>
                    </div>
                ) : error ? (
                    <div className="p-8 text-center">
                        <p className="text-red-600">{t('customers.error')}</p>
                        <button
                            onClick={() => refetch()}
                            className="mt-4 text-indigo-600 hover:text-indigo-800"
                        >
                            {t('customers.tryAgain')}
                        </button>
                    </div>
                ) : totalCustomers === 0 ? (
                    <div className="p-8 text-center">
                        <p className="text-gray-500">{t('customers.noCustomers')}</p>
                    </div>
                ) : (
                    <>
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        {t('customers.table.name')}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        {t('customers.table.email')}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        {t('customers.table.phone')}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        {t('customers.table.satisfaction')}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        {t('customers.table.status')}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        {t('customers.table.actions')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {paginatedCustomers.map((customer) => (
                                    <tr key={customer.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-4">
                                            <div className="font-medium text-gray-900">{customer.name}</div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="text-gray-900">{customer.email}</div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="text-gray-900">{customer.phone || '-'}</div>
                                        </td>
                                        <td className="px-4 py-4">
                                            {customer.satisfaction_score != null ? (
                                                <div className="flex items-center gap-2">
                                                    <span className={`font-medium ${
                                                        customer.satisfaction_score >= 4
                                                            ? 'text-green-600'
                                                            : customer.satisfaction_score >= 3
                                                            ? 'text-yellow-600'
                                                            : 'text-red-600'
                                                    }`}>
                                                        {customer.satisfaction_score.toFixed(1)}
                                                    </span>
                                                    <span className="text-gray-400">/ 5</span>
                                                </div>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-gray-400 text-xs" title={t('customers.satisfaction.unknownTip')}>
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    {t('customers.satisfaction.unknown')}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-4">
                                            {customer.banned ? (
                                                <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                                                    {t('customers.status.banned')}
                                                </span>
                                            ) : (
                                                <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                                    {t('customers.status.active')}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-4">
                                            <button
                                                onClick={() => navigate(`/dashboard/customers/${customer.id}`)}
                                                className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                                            >
                                                {t('customers.viewDetails')}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="px-4 py-3 border-t flex items-center justify-between">
                                <div className="text-sm text-gray-500">
                                    {t('customers.pagination.showing', {
                                        start: (currentPage - 1) * CUSTOMERS_PER_PAGE + 1,
                                        end: Math.min(currentPage * CUSTOMERS_PER_PAGE, totalCustomers),
                                        total: totalCustomers,
                                    })}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="px-3 py-1 rounded border text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                    >
                                        {t('customers.pagination.previous')}
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        className="px-3 py-1 rounded border text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                    >
                                        {t('customers.pagination.next')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default CustomerList;
